import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { Schema } from '@/shared/api/contract';
import { api } from '@/shared/api/client';
import type { ApiError } from '@/shared/api/errors';

/** Straight from the contract: cubas.domain.schemas.signature (contract v10.57). */
export type Signature = Schema<'SignatureRead'>;
export type SignatureSaved = Schema<'SignatureSaved'>;
export type Signatures = Schema<'SignaturesRead'>;
export type SignatureWrite = Schema<'SignatureWrite'>;

export const signatureKeys = { all: ['profile', 'signatures'] as const };

export const signaturesApi = {
  list: (): Promise<Signatures> => api.get<Signatures>('/api/profile/signatures'),
  save: (payload: SignatureWrite): Promise<SignatureSaved> =>
    api.put<SignatureSaved>('/api/profile/signatures', payload),
  remove: (mailbox: string | null): Promise<void> =>
    api.delete<void>(
      mailbox
        ? `/api/profile/signatures?mailbox=${encodeURIComponent(mailbox)}`
        : '/api/profile/signatures',
    ),
};

/** One's own signatures in HTML, one per mailbox and one for all of them. A back without
 * contract v10.57 answers 404: then there is nothing to show and the card is not there. */
export function useSignatures() {
  return useQuery<Signatures, ApiError>({
    queryKey: signatureKeys.all,
    queryFn: signaturesApi.list,
    retry: (count, error) => error.status !== 404 && count < 2,
  });
}

/** Saving or removing one changes what the next form of the mail manager is signed with. */
function useInvalidate() {
  const queryClient = useQueryClient();
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: signatureKeys.all }),
      queryClient.invalidateQueries({ queryKey: ['outgoing', 'defaults'] }),
    ]);
}

export function useSaveSignature() {
  const invalidate = useInvalidate();
  return useMutation<SignatureSaved, ApiError, SignatureWrite>({
    mutationFn: signaturesApi.save,
    onSuccess: () => invalidate(),
  });
}

export function useRemoveSignature() {
  const invalidate = useInvalidate();
  return useMutation<void, ApiError, string | null>({
    mutationFn: signaturesApi.remove,
    onSuccess: () => invalidate(),
  });
}
